import React, { CSSProperties, useEffect, useState } from 'react';
import useSuperscriptTags from '../../../hooks/useSuperscriptTags';
import useViewport from '../../../hooks/useViewport';
import { ComponentTypeNames } from '../../../schema';
import { DatoCmsHeroComponent } from '../../../schema/datocms';
import { DatoCmsLink, DatoCmsNewsletterSubscription } from '../../../schema/datocms/blocks';
import { Alignments, ImageAlignments } from '../../../schema/enum/alignments';
import { GradientClassMap } from '../../../schema/enum/gradients';
import { getFlexJustification, getTextAlignmentClass } from '../../../utilities';
import NewsLetterSubscriptionComponent from '../../block-components/newsletter-subscription-component/newsletter-subscription-component';
import Button from '../../button/button';
import HtmlEntityTextComponent from '../../html-entity-text/html-entity-text-component';
import Icon from '../../icon/icon';

const HeroComponent = (
    {
        title, titleAlignment, subtitle, subtitleAlignment, image, imageAlignment, contentAlignment,
        modularContent, modularContentAlignment, showReviewStars, mobileGradient, product
    }: DatoCmsHeroComponent
): React.ReactElement => {
    const minHeightStyle: string = '700px';
    const isBackgroundImage: boolean = imageAlignment === ImageAlignments.BACKGROUND;

    const tempGradientClass: string | undefined = GradientClassMap.find(g => g.gradient === mobileGradient)?.className;
    const gradientClass: string = tempGradientClass ? `bg-gradient-to-b bg-no-repeat ${tempGradientClass}` : '';

    let titleElement: JSX.Element | undefined = undefined;
    if (title) {
        titleElement = <p className={`text-primary text-center text-4xl font-bold lg:${getTextAlignmentClass(titleAlignment)}`}>
            { <HtmlEntityTextComponent text={title} /> }
        </p>;
    }

    let subtitleElement: JSX.Element | undefined = undefined;
    if (subtitle) {
        subtitleElement = <p className={`mt-6 text-xl md:text-2xl font-sans text-primary text-center lg:${getTextAlignmentClass(subtitleAlignment)}`}>
            { <HtmlEntityTextComponent text={subtitle} /> || subtitle }
        </p>;
    }

    let modularContentElement: JSX.Element | undefined = undefined;
    if (modularContent && modularContent.length > 0) {
        if (modularContent[0].__typename === ComponentTypeNames.LINK) {
            modularContentElement = (
                <div className={`mt-12 flex justify-center lg:${getFlexJustification(modularContentAlignment)}`}>
                    <Button {...modularContent[0] as DatoCmsLink} />
                </div>
            );
        } else if (modularContent[0].__typename === ComponentTypeNames.NEWSLETTER_SUBSCRIPTION) {
            modularContentElement = (
                <div className={`mt-12 flex justify-center lg:${getFlexJustification(modularContentAlignment)}`}>
                    <NewsLetterSubscriptionComponent {...modularContent[0] as DatoCmsNewsletterSubscription} />
                </div>
            );
        }
    } else if (showReviewStars) {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(<Icon name="star-filled" className="w-12 h-12" key={i} />);
        }

        modularContentElement = (
            <div role="img" aria-label="5 stars" className={`flex space-x-2 justify-center lg:${getFlexJustification(modularContentAlignment)} filter-yellow`}>
                {stars}
            </div>
        );
    }

    const backgroundImageStyle: CSSProperties = {
        backgroundImage: `url(${image?.url})`,
        minHeight: minHeightStyle
    };

    const { viewportWidth } = useViewport();

    const [isTabletOrMobile, setIsTabletOrMobile] = useState<boolean>(false);
    const [isDesktop, setIsDesktop] = useState<boolean>(false);
    const [isBigScreen, setIsBigScreen] = useState<boolean>(false);
    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

    useEffect(() => {
        if (viewportWidth <= 768) {
            setIsTabletOrMobile(true);
            setIsDesktop(false);
        } else {
            setIsDesktop(true);
            setIsTabletOrMobile(false);
        }

        if(viewportWidth <= 1024){
            setIsSmallScreen(true);
            setIsBigScreen(false);
        } else {
            setIsBigScreen(true);
            setIsSmallScreen(false);

        }

    }, [viewportWidth]);

    useSuperscriptTags('®', 'hero-content');

    function buildBackgroundImageHero(): React.ReactElement {
        let spacingClasses: string = '';
        let productSection: JSX.Element | undefined = undefined;
        if (product && product.headerLogoImage) {
            const contentIsLeftAligned: boolean = contentAlignment === Alignments.LEFT;
            spacingClasses = contentIsLeftAligned ? 'space-y-8 space-y-reverse md:space-x-32 md:space-y-0' : 'space-y-8 space-y-reverse md:space-x-32 md:space-x-reverse';

            const productOrderClass: string = contentIsLeftAligned ? 'order-first md:order-last' : 'order-first';
            productSection = (
                <div className={`theme-white ${productOrderClass}`}>
                    <div className="flex flex-col items-center">
                        <div>
                            <img style={{height: '12rem', width: '12rem'}} src={product.headerLogoImage.url} alt={product.headerLogoImage.alt} />
                        </div>
                        <label className="text-3xl md:text-4xl font-semibold font-serif text-white">{product.name}</label>
                    </div>
                </div>
            );
        }

        const backgroundHeroMainContent: JSX.Element = (
            <div className="container mx-auto" id="hero-content">
                <div className={`theme-white flex flex-col md:flex-row ${getFlexJustification(contentAlignment)} ${spacingClasses} items-center`}>
                    <div className="flex flex-col hero-content-overlay hero-overlay-border-none md:hero-overlay-border w-11/12 md:w-2/3">
                        { titleElement }
                        { subtitleElement }
                        { modularContentElement }
                    </div>

                    {productSection}
                </div>
            </div>
        );

        const backgroundDisplayClasses: string = mobileGradient ? 'hidden md:flex' : 'flex';
        const backgroundHeroContent = (
            <div className={`bg-cover bg-center bg-no-repeat ${backgroundDisplayClasses} bg-origin-border md:bg-origin-content hero-overlay-border md:hero-overlay-border-none items-center`} style={backgroundImageStyle} role="img" aria-label={image?.alt}>
                {backgroundHeroMainContent}
            </div>
        );

        let gradientHeroContent: JSX.Element | undefined = undefined;
        if (mobileGradient) {
            gradientHeroContent = (
                <div className={`flex items-center ${gradientClass} bg-origin-border hero-overlay-border`} style={ { minHeight: minHeightStyle } }>
                    {backgroundHeroMainContent}
                </div>
            );
        }

        // since background image is dynamically set via the style tag, the div needs to be hidden at smaller
        // screen sizes when using gradients
        return (
            <div>
                {isDesktop && backgroundHeroContent}
                {isTabletOrMobile && gradientHeroContent}
            </div>
        );
    }

    function buildTwoThirdsHero(): React.ReactElement {
        const imageContent: JSX.Element = (
            <div
                className="block w-2/3 bg-cover bg-center bg-no-repeat bg-origin-border side-image"
                style={backgroundImageStyle}
                role="img"
                aria-label={image?.alt}>
            </div>
        );

        const reverseClass: string = imageAlignment === ImageAlignments.LEFT ? 'flex-row-reverse' : '';

        const twoThirdsHero = (
            <div className="flex items-center">
                <div className={`theme-white w-full flex items-center ${reverseClass}`}>
                    <div className="flex flex-col w-1/3 px-8">
                        { titleElement }
                        { subtitleElement }
                        { modularContentElement }
                    </div>

                    {imageContent}
                </div>
            </div>
        );

        // the 2/3 layout only shows on screen sizes 1024px (lg) and above. Smaller screens move the image
        // to the background and text over the top of that
        return (
            <div>
                {isBigScreen && <div className="hidden lg:block">
                    {twoThirdsHero}
                </div>}
                {isSmallScreen && <div className="block lg:hidden">
                    {buildBackgroundImageHero()}
                </div>}
            </div>
        );
    }

    if (isBackgroundImage) {
        return buildBackgroundImageHero();
    } else {
        return buildTwoThirdsHero();
    }
};

export default HeroComponent;
